import React, { useEffect, useState } from 'react';
import axios from '../../config/axiosConfig';
import { MdArrowBack, MdCloudUpload, MdDelete, MdAdd } from 'react-icons/md';

const AddEvent = ({ onBack, onEventAdded }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);

    // Inline Category Creation
    const [showNewCatInput, setShowNewCatInput] = useState(false);
    const [newCatName, setNewCatName] = useState('');
    const [catLoading, setCatLoading] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get('nai-events/categories/');
            setCategories(response.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files) {
            setImages((prev) => [...prev, ...Array.from(e.target.files)]);
        }
    };

    const removeImage = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    const handleAddCategoryInline = async () => {
        if (!newCatName.trim()) return;
        setCatLoading(true);
        try {
            const response = await axios.post('nai-events/categories/', { name: newCatName });
            setCategories((prev) => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
            setSelectedCategory(response.data.id);
            setNewCatName('');
            setShowNewCatInput(false);
        } catch (error) {
            console.error("Error adding category inline:", error);
            alert("Category already exists or invalid data.");
        } finally {
            setCatLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('created_at', date ? new Date(date).toISOString() : '');
        if (selectedCategory) {
            formData.append('category', selectedCategory);
        }

        images.forEach((image) => {
            formData.append('images', image);
        });

        try {
            await axios.post('nai-events/events/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setLoading(false);
            setStatus({ type: 'success', message: 'Event published successfully!' });
            setTimeout(() => {
                onEventAdded();
            }, 1000);
        } catch (error) {
            console.error("Error adding event:", error);
            setLoading(false);
            setStatus({ type: 'error', message: 'Failed to publish event.' });
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 max-w-4xl mx-auto">
            <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-6 font-semibold">
                <MdArrowBack className="mr-2" /> Back to Events List
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Publish New Event</h2>

            {status && (
                <div className={`p-4 mb-6 rounded-xl text-sm font-semibold ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {status.message}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Event Title</label>
                        <input
                            type="text"
                            required
                            placeholder="e.g. National Media Conclave 2026"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-3"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Event Date</label>
                        <input
                            type="date"
                            required
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-3"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                    {!showNewCatInput ? (
                        <div className="flex gap-2">
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="flex-1 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-3 text-sm bg-white"
                            >
                                <option value="">-- Select Category (Optional) --</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => setShowNewCatInput(true)}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-4 py-3 rounded-xl border flex items-center gap-1 text-sm transition-colors"
                            >
                                <MdAdd /> Create New
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-2 bg-gray-55 p-3 rounded-xl border">
                            <input
                                type="text"
                                placeholder="New Category Name..."
                                value={newCatName}
                                onChange={(e) => setNewCatName(e.target.value)}
                                className="flex-1 rounded-lg border-gray-300 border p-2 text-sm"
                            />
                            <button
                                type="button"
                                disabled={catLoading}
                                onClick={handleAddCategoryInline}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg text-sm transition-colors"
                            >
                                {catLoading ? 'Saving...' : 'Add'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowNewCatInput(false)}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Content / Description</label>
                    <textarea
                        required
                        placeholder="Write detailed event highlights, speeches, announcements..."
                        rows={8}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-3 text-sm"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Event Images</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-blue-400 transition-colors">
                        <div className="space-y-1 text-center">
                            <MdCloudUpload className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600 justify-center">
                                <label htmlFor="events-upload" className="relative cursor-pointer bg-white rounded-md font-semibold text-blue-600 hover:text-blue-500 focus-within:outline-none">
                                    <span>Upload event images</span>
                                    <input 
                                        id="events-upload" 
                                        name="events-upload" 
                                        type="file" 
                                        className="sr-only" 
                                        onChange={handleImageChange} 
                                        accept="image/*" 
                                        multiple 
                                    />
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">Attach multiple photos of this event</p>
                        </div>
                    </div>

                    {/* Preview Selected Files */}
                    {images.length > 0 && (
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4">
                            {images.map((file, index) => (
                                <div key={index} className="relative group rounded-lg overflow-hidden border bg-gray-50 p-1 flex flex-col justify-between">
                                    <div className="w-full h-16 bg-blue-50 rounded flex items-center justify-center text-[10px] text-center break-all p-1 text-blue-700 font-medium overflow-hidden">
                                        {file.name}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition-colors"
                                    >
                                        <MdDelete className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-4 border-t">
                    <button
                        type="submit"
                        disabled={loading}
                        className={`bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-all transform hover:scale-[1.02] ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {loading ? 'Publishing...' : 'Publish Event'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddEvent;
